import type { SerializedStyles, Theme } from "@emotion/react";
import { faChevronDown, faChevronUp } from "@fortawesome/pro-regular-svg-icons";
import type { ReactNode } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { a as animated } from "react-spring";

import { Button, NoStyleButton } from "@aviary/components/Button";
import { l } from "@aviary/locales/i18n";
import type { AviarySize } from "@aviary/types";
import { FontAwesomeIcon } from "@shared/react-fontawesome/react-fontawesome";

import { useAccordionAnimation } from "./../useAccordionAnimation";

import * as styles from "./NoStyleAccordion.styles";

type AccordionAviarySize = Extract<AviarySize, "xsmall" | "small" | "medium" | "large">;
interface Props {
  /**
   * Specifies the core component shown in the collapsed accordion trigger
   *
   * @required
   */
  coreTriggerContent: ReactNode;
  /**
   * Specifies the secondary component shown in the collapsed accordion trigger
   *
   * @default null
   */
  secondaryTriggerContent?: ReactNode;
  /**
   * Specifies the height of the accordion
   *
   * @default medium
   */
  size?: AccordionAviarySize;
  /**
   * Specifies the position of the chevron
   *
   * @default end
   */
  chevronPosition?: "start" | "end";
  /**
   * Specifies whether or not the accordion is expanded by default
   *
   * @default false
   */
  isDefaultExpanded?: boolean;
  /**
   * Specifies whether or not the accordion is disabled
   *
   * @default false
   */
  isDisabled?: boolean;
  /**
   * Children to be rendered as the expanded content
   *
   * @default undefined
   */
  children?: ReactNode;
  /**
   * Callback when the accordion is opened/closed, which passes up the state
   */
  onChange?: (open: boolean) => void;
  /**
   * Custom styles for trigger area
   */
  triggerStyles?:
    | SerializedStyles
    | (SerializedStyles | ((theme: Theme) => SerializedStyles))[]
    | ((theme: Theme) => SerializedStyles);
  /**
   * Custom styles for expanded area
   */
  expandedStyles?:
    | SerializedStyles
    | (SerializedStyles | ((theme: Theme) => SerializedStyles))[]
    | ((theme: Theme) => SerializedStyles);
  /**
   * Conditionally uses the chevron icon as the trigger instead of triggerContent
   * Best for implementations with interactive trigger content like links
   * @default false
   */
  isChevronTrigger?: boolean;
  /**
   * Custom styles for the chevron trigger
   */
  chevronTriggerStyles?:
    | SerializedStyles
    | (SerializedStyles | ((theme: Theme) => SerializedStyles))[]
    | ((theme: Theme) => SerializedStyles);
}

/**
 * Documentation:
 * https://aviary.docs.fullscript.cloud/interaction/NoStyleAccordion/
 */
const NoStyleAccordion = ({
  coreTriggerContent,
  secondaryTriggerContent,
  children,
  onChange,
  size = "medium",
  chevronPosition = "end",
  isDefaultExpanded = false,
  isDisabled = false,
  triggerStyles,
  expandedStyles,
  isChevronTrigger = false,
  chevronTriggerStyles,
  ...rest
}: Props) => {
  const { t } = useTranslation();
  const [isHovered, setIsHovered] = useState(false);
  const { setIsExpanded, isExpanded, isAnimating, ref, height, opacity, y } = useAccordionAnimation(
    !isDisabled && isDefaultExpanded
  );
  const isIconExpanded = isExpanded ? faChevronUp : faChevronDown;

  const accordionWrapperStyles = !isDisabled ? styles.wrapper : undefined;

  const triggerButtonStyles = [
    styles.triggerButton.base,
    styles.padding[size],
    isChevronTrigger && isDisabled && styles.disabled,
    triggerStyles,
  ];

  const frameStyles = [
    styles.frame.base,
    isExpanded && !isAnimating && styles.frame.open,
    isHovered && !isExpanded && styles.frame.hovered,
    expandedStyles,
  ] as SerializedStyles[];

  const coreTriggerContentStyles = (): (SerializedStyles | undefined)[] | undefined => {
    if (chevronPosition === "end") {
      return [
        styles.triggerWrapper.coreContent,
        secondaryTriggerContent ? styles.triggerWrapper.bothContent : undefined,
      ];
    }
    if (chevronPosition === "start") {
      return [
        styles.triggerWrapper.coreContent,
        styles.triggerWrapper.withChevron,
        secondaryTriggerContent ? styles.triggerWrapper.bothContent : undefined,
      ];
    }
  };

  const secondaryTriggerContentStyles = (): SerializedStyles[] | undefined => {
    if (chevronPosition === "end" && secondaryTriggerContent) {
      return [styles.triggerWrapper.secondaryContent, styles.triggerWrapper.withChevron];
    }
  };

  const chevronTriggerButtonStyles = [styles.chevronButton, chevronTriggerStyles];

  const renderChevronIcon = () => {
    if (isChevronTrigger) {
      return (
        <Button
          isIcon
          isText
          isColor="system"
          size="xsmall"
          onClick={handleClick}
          aria-expanded={isExpanded}
          disabled={isDisabled}
          css={chevronTriggerButtonStyles}
          aria-label={t(l.aviary.accordion.trigger)}
        >
          <FontAwesomeIcon icon={isIconExpanded} css={styles.chevron} />
        </Button>
      );
    }

    return (
      <div css={styles.chevronWrapper}>
        <FontAwesomeIcon icon={isIconExpanded} css={styles.chevron} />
      </div>
    );
  };

  const renderTriggerContent = () => {
    if (chevronPosition === "end") {
      return (
        <div css={styles.triggerWrapper.base}>
          <div css={coreTriggerContentStyles()}>{coreTriggerContent}</div>
          <div css={secondaryTriggerContentStyles()}>
            {!!secondaryTriggerContent && secondaryTriggerContent}
            {renderChevronIcon()}
          </div>
        </div>
      );
    }

    if (chevronPosition === "start") {
      return (
        <div css={styles.triggerWrapper.base}>
          <div css={coreTriggerContentStyles()}>
            {renderChevronIcon()}
            {coreTriggerContent}
          </div>
          <div>{secondaryTriggerContent}</div>
        </div>
      );
    }
  };

  const renderExpandedArea = () => (
    <animated.div
      style={{
        opacity,
        height: isExpanded && !isAnimating ? "auto" : height,
      }}
      css={frameStyles}
    >
      <animated.div ref={ref} style={{ y }} css={styles.padding[size]}>
        {children}
      </animated.div>
    </animated.div>
  );

  const handleClick = () => {
    if (isDisabled) return;
    onChange?.(!isExpanded);
    setIsExpanded(!isExpanded);
  };

  if (isChevronTrigger) {
    return (
      <div css={accordionWrapperStyles} {...rest}>
        <div css={triggerButtonStyles}>{renderTriggerContent()}</div>
        {renderExpandedArea()}
      </div>
    );
  }

  return (
    <div
      css={accordionWrapperStyles}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      {...rest}
    >
      <NoStyleButton
        onClick={handleClick}
        disabled={isDisabled}
        css={triggerButtonStyles}
        aria-expanded={isExpanded}
      >
        {renderTriggerContent()}
      </NoStyleButton>
      {renderExpandedArea()}
    </div>
  );
};

export { NoStyleAccordion, type Props };
